import type { ReactNode } from 'react';
import { Button } from '@/components/base/Button';
import { t } from '@/messages/zh-TW';
import { ModalFrame } from '../ModalFrame';

/**
 * 共用對話框 ②：失敗／驗證訊息。
 *
 * 原系統函式：`showAlertDialog(content, callbackFunction, title = "警告")`，`js-my/common.js:340-354`
 *   `:341` `$.alert({ … })`
 *   `:342` `icon: 'fa fa-info-circle'`
 *   `:343` `title: title`（預設值 `"警告"`，見 `:340` 的預設參數）
 *   `:345` `type: 'red'`
 *   `:347` `closeIcon: true`
 *   `:348-352` 單一按鈕 `'確認'` → callbackFunction
 * 呼叫範例：`common.js:1071`（檔案上傳副檔名限制，內容含 `<br>` 多行）、`:1111`、`:1123`。
 *
 * ※ `zh-TW.ts` 的 `dialog.alertTitle` 值是「訊息」，與原系統的「警告」不同；
 *   本元件用 `dialog.alertTitle`（值已依原始碼校正為「警告」）。
 *   TODO(messages): 由 messages 負責人裁定 `dialog.alertTitle` 是否改成「警告」。
 *
 * `AUDIT_GATE3_HANDOFF.md` §1.1：這個對話框在全部線稿中未入稿。
 *
 * 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】
 */
export interface AlertDialogProps {
  uiId: string;
  open: boolean;
  /** 訊息內容。原系統允許夾 `<br>`（common.js:1071）；雛形用 ReactNode，不用 dangerouslySetInnerHTML。 */
  message: ReactNode;
  /** 覆寫標題，預設「警告」（common.js:340）。 */
  title?: string;
  onClose(): void;
}

export function AlertDialog({ uiId, open, message, title, onClose }: AlertDialogProps) {
  return (
    <ModalFrame
      uiId={uiId}
      open={open}
      title={title ?? t('dialog.alertTitle')}
      tone="danger"
      dismissible
      onClose={onClose}
      footer={
        <Button uiId={`${uiId}-ok`} variant="primary" size="sm" type="button" onClick={onClose}>
          {t('action.acknowledge')}
        </Button>
      }
    >
      <div data-ui-id={`${uiId}-message`}>{message}</div>
    </ModalFrame>
  );
}
